/**
 * snapshotService.ts
 *
 * Provides two main capabilities:
 *
 *  1. exportProductSnapshot(productId)
 *     Fetches all live data for a product from the API and returns a
 *     fully self-contained, ID-free ProductSnapshot.
 *
 *  2. buildImportPayload(snapshot, targetEnvData)
 *     Given a snapshot and the target environment's live data, resolves
 *     all composite-key references to real target-env IDs and returns:
 *       - A ready-to-send bulkInsert payload
 *       - A human-readable ImportSummary
 */

import { apiService } from './api';
import { getConfig } from '../config/runtimeConfig';
import {
  Product,
  Entitlement,
  OrgEntitlement,
  SKU,
  ProductSKUConfig,
  ProductSKUConfigEntitlement,
  SKUOrgEntitlementConfig,
  ProvisioningQuestion,
  ProductAllowedRegion,
} from '../types';
import {
  ProductSnapshot,
  SnapshotSKU,
  EntitlementRef,
  OrgEntitlementRef,
  ImportSummary,
  DeletePayload,
} from '../types/snapshot';

// ─────────────────────────────────────────────────────────────────────────────
// Raw API shapes used internally
// ─────────────────────────────────────────────────────────────────────────────

interface RawSkuConfig extends ProductSKUConfig {
  sku_id: number;
  product_id: number;
}

interface RawSkuConfigEntitlement extends ProductSKUConfigEntitlement {
  product_sku_config_sku_id: number;
  product_sku_config_product_id: number;
  entitlement_id: number;
}

interface RawSkuOrgEntitlementConfig extends SKUOrgEntitlementConfig {
  sku_id: number;
  org_entitlement_id: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. Export
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetches all data related to a product from the live API and assembles
 * a fully ID-free ProductSnapshot.
 */
export async function exportProductSnapshot(
  productId: number,
): Promise<ProductSnapshot> {
  // Fetch everything in parallel
  const [
    products,
    entitlements,
    orgEntitlements,
    skus,
    skuConfigs,
    skuConfigEntitlements,
    skuOrgConfigs,
    provisioningQuestions,
    allowedRegionsRaw,
  ] = await Promise.all([
    apiService.getProducts(),
    apiService.getEntitlements(),
    apiService.getOrgEntitlements(),
    apiService.getSKUs(),
    apiService.getProductSkuConfigs() as Promise<RawSkuConfig[]>,
    apiService.getProductSkuConfigEntitlements() as Promise<RawSkuConfigEntitlement[]>,
    apiService.getSKUOrgEntitlementConfigs() as Promise<RawSkuOrgEntitlementConfig[]>,
    apiService.getAllProvisioningQuestions(),
    // Fetch all allowed regions
    (async () => {
      const resp = await (apiService as unknown as {
        client: { get: (url: string) => Promise<{ data: { data: ProductAllowedRegion[] } }> };
      }).client.get('config/product_allowed_regions');
      return (resp.data.data ?? []) as ProductAllowedRegion[];
    })(),
  ]);

  const product = products.find((p) => p.id === productId);
  if (!product) {
    throw new Error(`Product with ID ${productId} not found`);
  }

  // ── Product fields ────────────────────────────────────────────────────────
  const productRecord = product as Product & Record<string, unknown>;
  const snapshotProduct = {
    name: product.name,
    key: product.key,
    provisioning_client: product.provisioning_client,
    provisioning_style: product.provisioning_style,
    is_hidden: productRecord.is_hidden as boolean | undefined,
    is_auto_provision: productRecord.is_auto_provision as boolean | undefined,
    is_composite: productRecord.is_composite as boolean | undefined,
    is_brownfield_only: productRecord.is_brownfield_only as boolean | undefined,
    is_unified_onboarding_supported: productRecord.is_unified_onboarding_supported as boolean | undefined,
    is_parent_provisioning_supported: productRecord.is_parent_provisioning_supported as boolean | undefined,
    inherit_parent_metadata: productRecord.inherit_parent_metadata as boolean | undefined,
  };

  // ── Entitlements ──────────────────────────────────────────────────────────
  const productEntitlements = entitlements.filter((e) => e.product_id === productId);
  const snapshotEntitlements = productEntitlements.map((e) => ({
    name: e.name,
    value: e.value,
    description: e.description,
    needs_quantity: e.needs_quantity,
    unit: e.unit,
  }));

  // ── Org Entitlements ──────────────────────────────────────────────────────
  const productOrgEntitlements = orgEntitlements.filter(
    (e) => e.product_id === productId,
  );
  // Org-entitlement natural keys are product-scoped. Some source environments
  // contain duplicate rows for the same name/value/access type; a portable
  // snapshot can represent that entity only once.
  const seenOrgEntitlementKeys = new Set<string>();
  const snapshotOrgEntitlements = productOrgEntitlements
    .filter((e) => {
      const key = `${e.name ?? ''}||${e.value ?? ''}||${e.access_type ?? ''}`;
      if (seenOrgEntitlementKeys.has(key)) { return false; }
      seenOrgEntitlementKeys.add(key);
      return true;
    })
    .map((e) => ({
      name: e.name,
      value: e.value,
      description: e.description,
      needs_quantity: e.needs_quantity,
      unit: e.unit,
      is_service: e.is_service,
      access_type: e.access_type,
    }));

  // ── Allowed Regions ───────────────────────────────────────────────────────
  const allowedRegions = allowedRegionsRaw
    .filter((r) => r.product_id === productId)
    .map((r) => r.region_name);

  // ── Provisioning Questions ────────────────────────────────────────────────
  const productQuestions = provisioningQuestions.filter(
    (q) => q.product_id === productId,
  );
  const snapshotQuestions = productQuestions.map(
    (q: ProvisioningQuestion) => ({
      name: q.name,
      display_name: q.display_name,
      description: q.description,
      type: q.type,
    }),
  );

  // ── SKUs ──────────────────────────────────────────────────────────────────
  // Collect all SKU IDs linked to this product
  const linkedSkuIds = Array.from(
    new Set(
      skuConfigs
        .filter((c) => c.product_id === productId)
        .map((c) => c.sku_id),
    ),
  );

  const snapshotSkus: SnapshotSKU[] = linkedSkuIds.map((skuId) => {
    const sku = skus.find((s) => s.id === skuId);
    const skuRecord = (sku ?? {}) as Record<string, unknown>;

    // Keep each product snapshot scoped to its own suite-SKU relationship.
    // Other products' links and entitlements are handled by their snapshots.
    const productLinksForSnapshotProduct = skuConfigs.filter(
      (c) => c.sku_id === skuId && c.product_id === productId,
    );

    const productLinks = productLinksForSnapshotProduct.map((cfg) => {
      const linkedProduct = products.find((p) => p.id === cfg.product_id);

      // Entitlements linked via product_sku_config_entitlements
      const linkedEntIds = skuConfigEntitlements
        .filter(
          (e) =>
            e.product_sku_config_sku_id === skuId &&
            e.product_sku_config_product_id === cfg.product_id,
        )
        .map((e) => e.entitlement_id);

      const linkedEntitlementRefs: EntitlementRef[] = linkedEntIds
        .map((eid): EntitlementRef | null => {
          const ent = entitlements.find((e) => e.id === eid);
          return ent ? { name: ent.name, value: ent.value, needs_quantity: ent.needs_quantity } : null;
        })
        .filter((r): r is EntitlementRef => r !== null);

      return {
        productRef: {
          name: linkedProduct?.name ?? `Product ${cfg.product_id}`,
          key: linkedProduct?.key,
        },
        operation: cfg.operation,
        operand: cfg.operand,
        force_region_name: cfg.force_region_name,
        linkedEntitlementRefs,
      };
    });

    // Org entitlement links for this SKU
    const orgEntIds = skuOrgConfigs
      .filter((c) => c.sku_id === skuId)
      .map((c) => c.org_entitlement_id);

    const orgEntitlementRefs: OrgEntitlementRef[] = orgEntIds
      .map((oeid) => {
        const oe = orgEntitlements.find((e) => e.id === oeid);
        return oe
          ? { name: oe.name, value: oe.value, access_type: oe.access_type }
          : null;
      })
      .filter((r): r is OrgEntitlementRef => r !== null);

    return {
      name: sku?.name ?? `SKU ${skuId}`,
      subscription_buying_model: skuRecord.subscription_buying_model as
        | string
        | undefined,
      is_external: (skuRecord.is_external as boolean | undefined) ?? false,
      productLinks,
      orgEntitlementRefs,
    };
  });

  return {
    version: '2.0',
    exportedAt: new Date().toISOString(),
    sourceEnv: getConfig().apiBaseUrl ?? 'unknown',
    product: snapshotProduct,
    entitlements: snapshotEntitlements,
    orgEntitlements: snapshotOrgEntitlements,
    allowedRegions,
    provisioningQuestions: snapshotQuestions,
    skus: snapshotSkus,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. Import — build bulkInsert payload
// ─────────────────────────────────────────────────────────────────────────────

export interface TargetEnvData {
  products: Product[];
  entitlements: Entitlement[];
  orgEntitlements: OrgEntitlement[];
  skus: SKU[];
  provisioningQuestions: ProvisioningQuestion[];
  productAllowedRegions: ProductAllowedRegion[];
  productSkuConfigs: ProductSKUConfig[];
  productSkuConfigEntitlements: ProductSKUConfigEntitlement[];
  skuOrgEntitlementConfigs: SKUOrgEntitlementConfig[];
}

export interface ImportPayloadResult {
  /** Records that don't yet exist → POST /config/bulk_insert */
  insertPayload: { bulk_entry: Array<{ table_key: string; entries: Array<{ data: Record<string, unknown> }> }> };
  /** Records that already exist and should be updated → PATCH /config/bulk_update (only populated for 'update' resolution) */
  updatePayload: { bulk_update: Array<{ table_key: string; updates: Array<{ primary_keys: Record<string, unknown>; data: Record<string, unknown> }> }> } | null;
  /** Orphan rows to delete from target in FK-safe order before upserts */
  deletePayload: DeletePayload;
  summary: ImportSummary;
}

/**
 * Resolves all composite-key references in a snapshot against the target
 * environment's live data and builds bulkInsert + (optionally) bulkUpdate payloads.
 *
 * Insertion order matters for FK constraints:
 *   products → entitlements → org_entitlements → provisioning_questions
 *   → product_allowed_regions → skus → product_sku_configs
 *   → product_sku_config_entitlements → sku_org_entitlement_configs
 *
 * Automatically resolves conflicts:
 *   - If the product already exists (matched by key or name) → update it and upsert all dependents
 *   - If the product is new → create it and insert all dependents
 */
export function buildImportPayload(
  snapshot: ProductSnapshot,
  target: TargetEnvData,
): ImportPayloadResult {
  const warnings: string[] = [];
  const insertEntry: Array<{ table_key: string; entries: Array<{ data: Record<string, unknown> }> }> = [];
  // bulk_update shape: { table_key, updates: [{ primary_keys: { ID }, data }] }
  const updateEntry: Array<{ table_key: string; updates: Array<{ primary_keys: Record<string, unknown>; data: Record<string, unknown> }> }> = [];

  // ── ID baselines ──────────────────────────────────────────────────────────
  const nextProductId =
    (target.products.length > 0
      ? Math.max(...target.products.map((p) => p.id ?? 0))
      : 0) + 1;

  const nextEntId =
    (target.entitlements.length > 0
      ? Math.max(...target.entitlements.map((e) => e.id ?? 0))
      : 0) + 1;

  const nextOrgEntId =
    (target.orgEntitlements.length > 0
      ? Math.max(...target.orgEntitlements.map((e) => e.id ?? 0))
      : 0) + 1;

  const nextPqId =
    (target.provisioningQuestions.length > 0
      ? Math.max(...target.provisioningQuestions.map((q) => q.id ?? 0))
      : 0) + 1;

  const nextSkuId =
    (target.skus.length > 0
      ? Math.max(...target.skus.map((s) => s.id ?? 0))
      : 0) + 1;

  // ── Product ───────────────────────────────────────────────────────────────
  const conflictingProduct = target.products.find((p) =>
    snapshot.product.key
      ? p.key === snapshot.product.key
      : p.name === snapshot.product.name,
  );

  let productId: number;
  let productCreated = false;
  let productUpdated = false;
  let productFieldDiffs: Record<string, { from: unknown; to: unknown }> = {};

  // Auto-detect: update if product already exists, create if it's new
  const isUpdate = !!conflictingProduct;

  if (conflictingProduct) {
    productId = conflictingProduct.id ?? 0;
    productFieldDiffs = diffProductFields(conflictingProduct, snapshot.product);
    if (Object.keys(productFieldDiffs).length > 0) {
      productUpdated = true;
      const productData = buildProductData(productId, snapshot.product);
      const { ID: productPK, ...productFields } = productData;
      updateEntry.push({
        table_key: 'products',
        updates: [{ primary_keys: { ID: productPK }, data: productFields }],
      });
    }
  } else {
    productId = nextProductId;
    productCreated = true;
    insertEntry.push({
      table_key: 'products',
      entries: [{ data: buildProductData(productId, snapshot.product) }],
    });
  }

  // ── Entitlements ──────────────────────────────────────────────────────────
  const entRefToId = new Map<string, number>();
  // Fallback lookup by name+value only — used to resolve refs from older (v2.0)
  // snapshots that don't carry needs_quantity. Such snapshots always have unique
  // name+value, so this stays unambiguous.
  const entNvToId = new Map<string, number>();
  let entIdCursor = nextEntId;
  let entitlementsCreated = 0;
  let entitlementsUpdated = 0;
  let entitlementsUnchanged = 0;

  const entInsert: Array<{ data: Record<string, unknown> }> = [];
  const entUpdate: Array<{ primary_keys: Record<string, unknown>; data: Record<string, unknown> }> = [];

  snapshot.entitlements.forEach((e) => {
    const existing = target.entitlements.find(
      (te) =>
        te.product_id === productId &&
        te.needs_quantity === e.needs_quantity &&
        (te.name !== null && te.name !== '' && e.name !== null && e.name !== ''
          ? te.name === e.name && te.value === e.value
          : te.name === null || te.name === ''
            ? te.value === e.value
            : te.name === e.name),
    );
    if (existing?.id !== undefined) {
      entRefToId.set(entKeyQ(e), existing.id);
      entNvToId.set(entRefKey(e), existing.id);
      if (isUpdate) {
        const changed =
          (existing.description ?? '') !== (e.description ?? '') ||
          existing.needs_quantity !== e.needs_quantity ||
          (existing.unit ?? '') !== (e.unit ?? '');
        if (changed) {
          entitlementsUpdated++;
          entUpdate.push({
            primary_keys: { ID: existing.id },
            data: {
              ProductID: productId,
              Name: e.name,
              Value: e.value,
              Description: e.description ?? '',
              NeedsQuantity: e.needs_quantity,
              Unit: e.unit ?? '',
            },
          });
        } else {
          entitlementsUnchanged++;
        }
      } else {
        entitlementsUnchanged++;
      }
    } else {
      const newId = entIdCursor++;
      entRefToId.set(entKeyQ(e), newId);
      entNvToId.set(entRefKey(e), newId);
      entitlementsCreated++;
      entInsert.push({
        data: {
          ID: newId,
          ProductID: productId,
          Name: e.name,
          Value: e.value,
          Description: e.description ?? '',
          NeedsQuantity: e.needs_quantity,
          Unit: e.unit ?? '',
        },
      });
    }
  });

  if (entInsert.length > 0) {
    insertEntry.push({ table_key: 'entitlements', entries: entInsert });
  }
  if (entUpdate.length > 0) {
    updateEntry.push({ table_key: 'entitlements', updates: entUpdate });
  }

  // Resolve a SKU-linked entitlement ref to a snapshot-scoped id. New snapshots
  // carry needs_quantity for exact disambiguation of same name+value entitlements;
  // older ones (unique name+value) fall back to a name+value lookup.
  const resolveSnapEntId = (ref: EntitlementRef): number | undefined => {
    if (ref.needs_quantity !== undefined) {
      const hit = entRefToId.get(entKeyQ(ref));
      if (hit !== undefined) { return hit; }
    }
    return entNvToId.get(entRefKey(ref));
  };
  // ── Org Entitlements ──────────────────────────────────────────────────────
  const orgEntRefToId = new Map<string, number>();
  let orgEntIdCursor = nextOrgEntId;
  let orgEntitlementsCreated = 0;
  let orgEntitlementsUpdated = 0;
  let orgEntitlementsUnchanged = 0;

  const orgEntInsert: Array<{ data: Record<string, unknown> }> = [];
  const orgEntUpdate: Array<{ primary_keys: Record<string, unknown>; data: Record<string, unknown> }> = [];

  const seenOrgEntitlementKeys = new Set<string>();
  snapshot.orgEntitlements.forEach((e) => {
    const naturalKey = `${e.name ?? ''}||${e.value ?? ''}||${e.access_type ?? ''}`;
    if (seenOrgEntitlementKeys.has(naturalKey)) {
      warnings.push(
        `Duplicate org entitlement "${e.name}"/"${e.value}" (${e.access_type}) in snapshot — repeated entry skipped.`,
      );
      return;
    }
    seenOrgEntitlementKeys.add(naturalKey);

    const existing = target.orgEntitlements.find(
      (te) =>
        te.product_id === productId &&
        (te.name !== null && te.name !== '' && e.name !== null && e.name !== ''
          ? te.name === e.name && te.value === e.value
          : te.name === null || te.name === ''
            ? te.value === e.value
            : te.name === e.name),
    );
    if (existing?.id !== undefined) {
      orgEntRefToId.set(
        orgEntRefKey({ name: e.name, value: e.value }),
        existing.id,
      );
      if (isUpdate) {
        const changed =
          (existing.description ?? '') !== (e.description ?? '') ||
          existing.needs_quantity !== e.needs_quantity ||
          (existing.unit ?? '') !== (e.unit ?? '') ||
          existing.is_service !== e.is_service;
        if (changed) {
          orgEntitlementsUpdated++;
          orgEntUpdate.push({
            primary_keys: { ID: existing.id },
            data: {
              ProductID: productId,
              Name: e.name,
              Value: e.value,
              Description: e.description ?? '',
              NeedsQuantity: e.needs_quantity,
              Unit: e.unit ?? '',
              IsService: e.is_service,
              AccessType: e.access_type,
            },
          });
        } else {
          orgEntitlementsUnchanged++;
        }
      } else {
        orgEntitlementsUnchanged++;
      }
    } else {
      const newId = orgEntIdCursor++;
      orgEntRefToId.set(
        orgEntRefKey({ name: e.name, value: e.value }),
        newId,
      );
      orgEntitlementsCreated++;
      orgEntInsert.push({
        data: {
          ID: newId,
          ProductID: productId,
          Name: e.name,
          Value: e.value,
          Description: e.description ?? '',
          NeedsQuantity: e.needs_quantity,
          Unit: e.unit ?? '',
          IsService: e.is_service,
          AccessType: e.access_type,
        },
      });
    }
  });

  if (orgEntInsert.length > 0) {
    insertEntry.push({ table_key: 'org_entitlements', entries: orgEntInsert });
  }
  if (orgEntUpdate.length > 0) {
    updateEntry.push({ table_key: 'org_entitlements', updates: orgEntUpdate });
  }

  // ── Provisioning Questions ────────────────────────────────────────────────
  let pqIdCursor = nextPqId;
  let questionsCreated = 0;
  let questionsUpdated = 0;
  let questionsUnchanged = 0;

  const pqInsert: Array<{ data: Record<string, unknown> }> = [];
  const pqUpdate: Array<{ primary_keys: Record<string, unknown>; data: Record<string, unknown> }> = [];

  snapshot.provisioningQuestions.forEach((q) => {
    const existing = target.provisioningQuestions.find(
      (tq) => tq.name === q.name && tq.product_id === productId,
    );
    if (existing?.id !== undefined) {
      if (isUpdate) {
        const changed =
          (existing.display_name ?? '') !== (q.display_name ?? '') ||
          (existing.description ?? '') !== (q.description ?? '') ||
          existing.type !== q.type;
        if (changed) {
          questionsUpdated++;
          pqUpdate.push({
            primary_keys: { ID: existing.id },
            data: {
              ProductID: productId,
              Name: q.name,
              DisplayName: q.display_name ?? '',
              Description: q.description ?? '',
              Type: q.type,
            },
          });
        } else {
          questionsUnchanged++;
        }
      } else {
        questionsUnchanged++;
      }
    } else {
      questionsCreated++;
      pqInsert.push({
        data: {
          ID: pqIdCursor++,
          ProductID: productId,
          Name: q.name,
          DisplayName: q.display_name ?? '',
          Description: q.description ?? '',
          Type: q.type,
        },
      });
    }
  });

  if (pqInsert.length > 0) {
    insertEntry.push({ table_key: 'provisioning_questions', entries: pqInsert });
  }
  if (pqUpdate.length > 0) {
    updateEntry.push({ table_key: 'provisioning_questions', updates: pqUpdate });
  }

  // ── Allowed Regions — only insert missing ones ────────────────────────────
  let regionsLinked = 0;
  let regionsAlreadyPresent = 0;

  if (snapshot.allowedRegions.length > 0) {
    const regionEntries: Array<{ data: Record<string, unknown> }> = [];
    snapshot.allowedRegions.forEach((region) => {
      const alreadyLinked = target.productAllowedRegions.some(
        (r) => r.product_id === productId && r.region_name === region,
      );
      if (alreadyLinked) {
        regionsAlreadyPresent++;
      } else {
        regionEntries.push({ data: { ProductID: productId, RegionName: region } });
        regionsLinked++;
      }
    });
    if (regionEntries.length > 0) {
      insertEntry.push({ table_key: 'product_allowed_regions', entries: regionEntries });
    }
  }

  // ── SKUs ──────────────────────────────────────────────────────────────────
  const skuNameToId = new Map<string, number>();
  let skuIdCursor = nextSkuId;

  const skuInsert: Array<{ data: Record<string, unknown> }> = [];
  const skuConfigEntries: Array<{ data: Record<string, unknown> }> = [];
  const skuConfigEntitlementEntries: Array<{ data: Record<string, unknown> }> = [];
  const skuOrgEntConfigEntries: Array<{ data: Record<string, unknown> }> = [];

  snapshot.skus.forEach((sku) => {
    let skuId: number;
    if (skuNameToId.has(sku.name)) {
      skuId = skuNameToId.get(sku.name) as number;
    } else {
      const existingSku = target.skus.find((s) => s.name === sku.name);
      if (existingSku?.id !== undefined) {
        skuId = existingSku.id;
      } else {
        skuId = skuIdCursor++;
        skuInsert.push({
          data: {
            ID: skuId,
            Name: sku.name,
            SubscriptionBuyingModel: sku.subscription_buying_model ?? '',
            IsExternal: sku.is_external,
          },
        });
      }
      skuNameToId.set(sku.name, skuId);
    }

    sku.productLinks.forEach((link) => {
      const isCurrentProduct =
        link.productRef.name === snapshot.product.name &&
        (link.productRef.key === undefined ||
          link.productRef.key === snapshot.product.key);
      if (!isCurrentProduct) { return; }

      const linkProductId = productId;

      const skuConfigAlreadyLinked = target.productSkuConfigs.some(
        (c) => c.product_id === linkProductId && c.sku_id === skuId,
      );
      if (!skuConfigAlreadyLinked) {
        skuConfigEntries.push({
          data: {
            ProductID: linkProductId,
            SkuID: skuId,
            ...(link.operation ? { Operation: link.operation } : {}),
            ...(link.operand !== undefined ? { Operand: link.operand } : {}),
            ...(link.force_region_name ? { ForceRegionName: link.force_region_name } : {}),
          },
        });
      }

      link.linkedEntitlementRefs.forEach((ref) => {
        const entId = resolveSnapEntId(ref);
        const resolvedEntId = entId ?? target.entitlements.find(
          (e) =>
            e.product_id === linkProductId &&
            (ref.needs_quantity === undefined || e.needs_quantity === ref.needs_quantity) &&
            (e.name !== null && e.name !== '' && ref.name !== null && ref.name !== ''
              ? e.name === ref.name && e.value === ref.value
              : e.name === null || e.name === ''
                ? e.value === ref.value
                : e.name === ref.name),
        )?.id;

        if (resolvedEntId === undefined) {
          warnings.push(
            `Entitlement "${ref.name}"/"${ref.value}" for product ID ${linkProductId} ` +
              `not found in target env — SKU entitlement link skipped.`,
          );
          return;
        }

        const skuEntAlreadyLinked = target.productSkuConfigEntitlements.some(
          (ce) =>
            ce.product_sku_config_product_id === linkProductId &&
            ce.product_sku_config_sku_id === skuId &&
            ce.entitlement_id === resolvedEntId,
        );
        if (!skuEntAlreadyLinked) {
          skuConfigEntitlementEntries.push({
            data: {
              ProductSkuConfigProductID: linkProductId,
              ProductSkuConfigSkuID: skuId,
              EntitlementID: resolvedEntId,
            },
          });
        }
      });
    });

    sku.orgEntitlementRefs.forEach((ref) => {
      const oeId = orgEntRefToId.get(orgEntRefKey(ref));
      const resolvedOeId = oeId ?? target.orgEntitlements.find(
        (e) =>
          (e.name !== null && e.name !== '' && ref.name !== null && ref.name !== ''
            ? e.name === ref.name && e.value === ref.value
            : e.name === null || e.name === ''
              ? e.value === ref.value
              : e.name === ref.name),
      )?.id;

      if (resolvedOeId === undefined) {
        warnings.push(
          `OrgEntitlement "${ref.name}"/"${ref.value}" (${ref.access_type}) ` +
            `not found in target env — SKU org entitlement link skipped.`,
        );
        return;
      }

      const oeAlreadyLinked = target.skuOrgEntitlementConfigs.some(
        (c) => c.sku_id === skuId && c.org_entitlement_id === resolvedOeId,
      );
      if (!oeAlreadyLinked) {
        skuOrgEntConfigEntries.push({ data: { SkuID: skuId, OrgEntitlementID: resolvedOeId } });
      }
    });
  });

  if (skuInsert.length > 0) {
    insertEntry.push({ table_key: 'skus', entries: skuInsert });
  }
  if (skuConfigEntries.length > 0) {
    insertEntry.push({ table_key: 'product_sku_configs', entries: skuConfigEntries });
  }
  if (skuConfigEntitlementEntries.length > 0) {
    insertEntry.push({ table_key: 'product_sku_config_entitlements', entries: skuConfigEntitlementEntries });
  }
  if (skuOrgEntConfigEntries.length > 0) {
    insertEntry.push({ table_key: 'sku_org_entitlement_configs', entries: skuOrgEntConfigEntries });
  }

  // ── Orphan deletions (update mode only) ──────────────────────────────────
  //
  // Any entity that exists in the target scoped to this product but is absent
  // from the snapshot was intentionally removed in source → delete from target.
  // Deletion order is FK-safe (children before parents).

  const deletePayload: DeletePayload = {
    skuOrgEntitlementConfigsToDelete: [],
    skuConfigEntitlementsToDelete: [],
    skuLinksToRemove: [],
    otherProductLinksToRemove: [],
    regionsToDelete: [],
    questionsToDelete: [],
    orgEntitlementsToDelete: [],
    entitlementsToDelete: [],
  };

  if (isUpdate) {
    // Build lookup sets from the snapshot (natural keys)
    const snapEntKeys = new Set(
      snapshot.entitlements.map((e) => entKeyQ(e)),
    );
    const snapOrgEntKeys = new Set(
      snapshot.orgEntitlements.map((e) => entRefKey(e)),
    );
    const snapQuestionNames = new Set(snapshot.provisioningQuestions.map((q) => q.name));
    const snapRegions = new Set(snapshot.allowedRegions);
    // snapshot SKU names that have a link to this product
    const snapSkuNamesForThisProduct = new Set(
      snapshot.skus
        .filter((sku) =>
          sku.productLinks.some(
            (pl) =>
              pl.productRef.name === snapshot.product.name &&
              (pl.productRef.key === undefined || pl.productRef.key === snapshot.product.key),
          ),
        )
        .map((sku) => sku.name),
    );

    // 1. Orphan sku_org_entitlement_configs — for any sku linked to this product,
    //    remove org-ent links whose org-entitlement is no longer in the snapshot SKU's orgEntitlementRefs
    const linkedSkuIds = target.productSkuConfigs
      .filter((c) => c.product_id === productId)
      .map((c) => c.sku_id);

    for (const skuId of linkedSkuIds) {
      const targetSku = target.skus.find((s) => s.id === skuId);
      const snapSku = targetSku ? snapshot.skus.find((s) => s.name === targetSku.name) : undefined;
      // Build a set of org-ent name:value:access_type refs that the snapshot wants on this SKU
      const snapOrgEntRefKeys = new Set(
        (snapSku?.orgEntitlementRefs ?? []).map(
          (r) => `${r.name}||${r.value}||${r.access_type ?? ''}`,
        ),
      );
      const orgConfigs = target.skuOrgEntitlementConfigs.filter(
        (c) => c.sku_id === skuId,
      );
      for (const cfg of orgConfigs) {
        const oe = target.orgEntitlements.find((e) => e.id === cfg.org_entitlement_id);
        if (!oe) { continue; }
        const key = `${oe.name ?? ''}||${oe.value ?? ''}||${oe.access_type ?? ''}`;
        if (!snapOrgEntRefKeys.has(key)) {
          const alreadyQueued = deletePayload.skuOrgEntitlementConfigsToDelete.some(
            (d) => d.skuId === skuId && d.orgEntitlementId === cfg.org_entitlement_id,
          );
          if (!alreadyQueued) {
            deletePayload.skuOrgEntitlementConfigsToDelete.push({
              skuId,
              orgEntitlementId: cfg.org_entitlement_id,
            });
          }
        }
      }
    }

    // 2. Orphan product_sku_config_entitlements — for each existing sku link to
    //    this product, remove entitlement links that are gone from the snapshot
    const existingSkuConfigs = target.productSkuConfigs.filter(
      (c) => c.product_id === productId,
    );
    for (const cfg of existingSkuConfigs) {
      const targetSku = target.skus.find((ts) => ts.id === cfg.sku_id);
      const snapSku = targetSku ? snapshot.skus.find((s) => s.name === targetSku.name) : undefined;

      // Check orphan CEs for the MAIN product link
      const ceRows = target.productSkuConfigEntitlements.filter(
        (ce) =>
          ce.product_sku_config_product_id === productId &&
          ce.product_sku_config_sku_id === cfg.sku_id,
      );
      for (const ce of ceRows) {
        const ent = target.entitlements.find((e) => e.id === ce.entitlement_id);
        if (!ent) { continue; }
        const snapLink = snapSku?.productLinks.find(
          (pl) =>
            pl.productRef.name === snapshot.product.name &&
            (pl.productRef.key === undefined || pl.productRef.key === snapshot.product.key),
        );
        const stillLinked = snapLink?.linkedEntitlementRefs.some(
          (ref) => ref.name === (ent.name ?? '') && ref.value === (ent.value ?? '') &&
            (ref.needs_quantity === undefined || ref.needs_quantity === ent.needs_quantity),
        );
        if (!stillLinked) {
          deletePayload.skuConfigEntitlementsToDelete.push({
            productId,
            skuId: cfg.sku_id,
            entitlementId: ce.entitlement_id,
          });
        }
      }

    }

    // 3. Orphan product_sku_configs (SKU links) — sku links to this product whose
    //    SKU name is no longer in the snapshot
    for (const cfg of existingSkuConfigs) {
      const targetSku = target.skus.find((s) => s.id === cfg.sku_id);
      if (!targetSku) { continue; }
      if (!snapSkuNamesForThisProduct.has(targetSku.name)) {
        deletePayload.skuLinksToRemove.push({
          productId,
          skuId: cfg.sku_id,
          skuName: targetSku.name,
        });
      }
    }

    // 4. Orphan product_allowed_regions
    const existingRegions = target.productAllowedRegions.filter(
      (r) => r.product_id === productId,
    );
    for (const r of existingRegions) {
      if (!snapRegions.has(r.region_name)) {
        deletePayload.regionsToDelete.push({
          productId,
          regionName: r.region_name,
        });
      }
    }

    // 5. Orphan provisioning_questions
    const existingQuestions = target.provisioningQuestions.filter(
      (q) => q.product_id === productId,
    );
    for (const q of existingQuestions) {
      if (!snapQuestionNames.has(q.name) && q.id !== undefined) {
        deletePayload.questionsToDelete.push({ id: q.id, name: q.name });
      }
    }

    // 6. Orphan org_entitlements
    const existingOrgEnts = target.orgEntitlements.filter(
      (e) => e.product_id === productId,
    );
    for (const e of existingOrgEnts) {
      const key = entRefKey({ name: e.name ?? '', value: e.value ?? '' });
      if (!snapOrgEntKeys.has(key) && e.id !== undefined) {
        deletePayload.orgEntitlementsToDelete.push({
          id: e.id,
          name: e.name ?? '',
          value: e.value ?? '',
        });
      }
    }

    // 7. Orphan entitlements
    const existingEnts = target.entitlements.filter(
      (e) => e.product_id === productId,
    );
    for (const e of existingEnts) {
      const key = entKeyQ({ name: e.name ?? '', value: e.value ?? '', needs_quantity: e.needs_quantity });
      if (!snapEntKeys.has(key) && e.id !== undefined) {
        deletePayload.entitlementsToDelete.push({
          id: e.id,
          name: e.name ?? '',
          value: e.value ?? '',
          needs_quantity: e.needs_quantity,
        });
      }
    }
  }

  // ── Summary ───────────────────────────────────────────────────────────────
  const summary: ImportSummary = {
    productCreated,
    productUpdated,
    productFieldDiffs,
    entitlementsCreated,
    entitlementsUpdated,
    entitlementsUnchanged,
    entitlementsDeleted: deletePayload.entitlementsToDelete.length,
    orgEntitlementsCreated,
    orgEntitlementsUpdated,
    orgEntitlementsUnchanged,
    orgEntitlementsDeleted: deletePayload.orgEntitlementsToDelete.length,
    regionsLinked,
    regionsAlreadyPresent,
    regionsDeleted: deletePayload.regionsToDelete.length,
    questionsCreated,
    questionsUpdated,
    questionsUnchanged,
    questionsDeleted: deletePayload.questionsToDelete.length,
    skusCreated: skuInsert.length,
    skuLinksCreated: skuConfigEntries.length,
    skuLinksRemoved: deletePayload.skuLinksToRemove.length,
    unresolvedRefs: warnings,
  };

  return {
    insertPayload: { bulk_entry: insertEntry },
    updatePayload: updateEntry.length > 0 ? { bulk_update: updateEntry } : null,
    deletePayload,
    summary,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Compare product fields; returns a map of fields that differ { from: existingValue, to: snapshotValue }. */
function diffProductFields(
  existing: Product,
  snap: ProductSnapshot['product'],
): Record<string, { from: unknown; to: unknown }> {
  const diffs: Record<string, { from: unknown; to: unknown }> = {};
  const check = (key: string, extVal: unknown, snapVal: unknown) => {
    const a = extVal ?? null;
    const b = snapVal ?? null;
    if (a !== b) {diffs[key] = { from: a, to: b };}
  };
  check('provisioning_client', existing.provisioning_client, snap.provisioning_client);
  check('provisioning_style', existing.provisioning_style, snap.provisioning_style);
  const ext = existing as unknown as Record<string, unknown>;
  check('is_hidden', ext['is_hidden'], snap.is_hidden ?? false);
  check('is_auto_provision', ext['is_auto_provision'], snap.is_auto_provision ?? false);
  check('is_composite', ext['is_composite'], snap.is_composite ?? false);
  check('is_brownfield_only', ext['is_brownfield_only'], snap.is_brownfield_only ?? false);
  check('is_unified_onboarding_supported', ext['is_unified_onboarding_supported'], snap.is_unified_onboarding_supported ?? false);
  check('is_parent_provisioning_supported', ext['is_parent_provisioning_supported'], snap.is_parent_provisioning_supported ?? false);
  check('inherit_parent_metadata', ext['inherit_parent_metadata'], snap.inherit_parent_metadata ?? false);
  return diffs;
}

function entRefKey(ref: { name: string; value: string }): string {
  const hasName = ref.name !== null && ref.name !== '';
  const hasValue = ref.value !== null && ref.value !== '';
  if (hasName && hasValue) { return `${ref.name}||${ref.value}`; }
  if (!hasName) { return `__noname__||${ref.value}`; }
  return `${ref.name}||__noval__`;
}

// name+value+needs_quantity key — disambiguates entitlements that share name+value.
function entKeyQ(ref: { name: string; value: string; needs_quantity?: boolean }): string {
  return `${entRefKey(ref)}||nq:${ref.needs_quantity === undefined ? 'any' : String(ref.needs_quantity)}`;
}

function orgEntRefKey(ref: { name: string; value: string }): string {
  return entRefKey(ref); // same conditional logic, access_type is NOT part of the key
}

function buildProductData(
  id: number,
  p: ProductSnapshot['product'],
): Record<string, unknown> {
  return {
    ID: id,
    Name: p.name,
    ...(p.key ? { Key: p.key } : {}),
    ...(p.provisioning_client ? { ProvisioningClient: p.provisioning_client } : {}),
    ...(p.provisioning_style ? { ProvisioningStyle: p.provisioning_style } : {}),
    IsHidden: p.is_hidden ?? false,
    IsAutoProvision: p.is_auto_provision ?? false,
    IsComposite: p.is_composite ?? false,
    IsBrownfieldOnly: p.is_brownfield_only ?? false,
    IsUnifiedOnboardingSupported: p.is_unified_onboarding_supported ?? false,
    IsParentProvisioningSupported: p.is_parent_provisioning_supported ?? false,
    InheritParentMetadata: p.inherit_parent_metadata ?? false,
  };
}
